import React, { useEffect, useMemo, useState } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { getLiveCollection } from '../../utils/liveData';
import { PRODUCT_TYPES, ORDER_STATUS_LABELS } from '../../constants';
import { formatPrice, formatDate, normalizeSearchText, getOrderQuantity } from '../../utils';
import ModernSelect from '../common/ModernSelect';
import './OrderCosts.css';

/*
 * Sipariş Maliyetleri (yalnızca kontrol amaçlı)
 * Her müşterinin genel toplamını (net), sipariş maliyetlerini ve net kazancını listeler.
 * Müşteri bazında elle girilen atölye / montaj maliyetleri net kazançtan düşülür.
 * Bu rakamlar başka hiçbir hesaplamaya (anasayfa, giderler vb.) eklenmez.
 */

const PAGE_SIZE = 20;

const toDate = (value) => {
  if (!value) return null;
  const date = value?.toDate ? value.toDate() : new Date(value);
  return isNaN(date.getTime()) ? null : date;
};

const getProductName = (order) => {
  if (order.productType === 'plise') {
    const name1 = order.pliseProduct1Name || order.productName || '';
    return order.pliseProduct2Name ? `${name1} + ${order.pliseProduct2Name}` : name1;
  }
  return `${order.productName || ''}${order.productVariant ? ` - ${order.productVariant}` : ''}`.trim();
};

// Sipariş tutarı: iskontolu fiyat varsa o (anasayfadaki ile aynı kural)
const getOrderAmount = (order) => (
  order.discountedPrice && parseFloat(order.discount) > 0
    ? parseFloat(order.discountedPrice) || 0
    : parseFloat(order.totalPrice) || 0
);

const getOrderCost = (order) => parseFloat(order.totalCost) || 0;

// Müşterinin genel toplamı (net) — müşteri listesi / PDF ile aynı hiyerarşi:
// Manuel Genel Toplam > Vade Farkı > Genel İskonto > Ürün İndirimi > Ara Toplam
const getCustomerGrandTotal = (customer, customerOrders) => {
  if (customer.manualGrandTotal !== undefined && customer.manualGrandTotal !== null && customer.manualGrandTotal !== '') {
    return parseFloat(customer.manualGrandTotal) || 0;
  }
  const totalAmount = customerOrders.reduce((s, o) => s + (parseFloat(o.totalPrice) || 0), 0);
  const discountedTotalAmount = customerOrders.reduce((s, o) => s + getOrderAmount(o), 0);
  const vadeliYuzde = parseFloat(customer.vadeliYuzde) || 0;
  const genelIskontoYuzde = parseFloat(customer.genelIskontoYuzde) || 0;
  if (vadeliYuzde > 0) return totalAmount + (totalAmount * vadeliYuzde / 100);
  if (genelIskontoYuzde > 0) return totalAmount - (totalAmount * genelIskontoYuzde / 100);
  if (discountedTotalAmount !== totalAmount && discountedTotalAmount > 0) return discountedTotalAmount;
  return totalAmount;
};

// "1.250,50" / "1250.5" gibi girişleri sayıya çevirir
const parseAmountInput = (value) => {
  const raw = String(value ?? '').trim();
  if (!raw) return 0;
  const isThousandsOnly = /^\d{1,3}(\.\d{3})+$/.test(raw); // "1.250" → 1250
  const normalized = raw.includes(',') || isThousandsOnly ? raw.replace(/\./g, '').replace(',', '.') : raw;
  const num = parseFloat(normalized);
  return isNaN(num) || num < 0 ? 0 : num;
};

const toInputValue = (num) => (num > 0 ? String(num).replace('.', ',') : '');

// Atölye / montaj maliyeti girişi — yazarken net kazanç anında güncellenir
const ExtraCostsEditor = ({ row, onSaved }) => {
  const [atolye, setAtolye] = useState(() => toInputValue(row.atolyeCost));
  const [montaj, setMontaj] = useState(() => toInputValue(row.montajCost));
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const atolyeNum = parseAmountInput(atolye);
  const montajNum = parseAmountInput(montaj);
  const dirty = atolyeNum !== row.atolyeCost || montajNum !== row.montajCost;
  const profit = row.amount - row.cost - atolyeNum - montajNum;

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      await updateDoc(doc(db, 'customers', row.id), {
        atolyeMaliyeti: atolyeNum,
        montajMaliyeti: montajNum,
        updatedAt: new Date()
      });
      onSaved(row.id, { atolyeMaliyeti: atolyeNum, montajMaliyeti: montajNum });
      setMessage({ type: 'ok', text: 'Kaydedildi' });
    } catch (err) {
      console.error('Ek maliyetler kaydedilemedi:', err);
      setMessage({ type: 'error', text: 'Kaydedilemedi, tekrar deneyin' });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setAtolye(toInputValue(row.atolyeCost));
    setMontaj(toInputValue(row.montajCost));
    setMessage(null);
  };

  return (
    <div className="oc-extra">
      <div className="oc-extra-inputs">
        <label className="oc-extra-field oc-extra-atolye">
          <span>Atölye maliyeti</span>
          <span className="oc-extra-input">
            <input
              type="text"
              inputMode="decimal"
              placeholder="0"
              value={atolye}
              onChange={(e) => { setAtolye(e.target.value); setMessage(null); }}
            />
            <span aria-hidden="true">₺</span>
          </span>
        </label>
        <label className="oc-extra-field oc-extra-montaj">
          <span>Montaj maliyeti</span>
          <span className="oc-extra-input">
            <input
              type="text"
              inputMode="decimal"
              placeholder="0"
              value={montaj}
              onChange={(e) => { setMontaj(e.target.value); setMessage(null); }}
            />
            <span aria-hidden="true">₺</span>
          </span>
        </label>
        <div className="oc-extra-actions">
          {dirty && (
            <button type="button" className="oc-extra-reset" onClick={handleReset} disabled={saving}>Vazgeç</button>
          )}
          <button type="button" className="oc-extra-save" onClick={handleSave} disabled={!dirty || saving}>
            {saving ? 'Kaydediliyor...' : 'Kaydet'}
          </button>
        </div>
      </div>
      {message && <span className={`oc-extra-msg is-${message.type}`} role="status">{message.text}</span>}

      <dl className="oc-breakdown">
        <div><dt>Genel toplam (net)</dt><dd>{formatPrice(row.amount)} ₺</dd></div>
        <div><dt>Sipariş maliyeti</dt><dd>− {formatPrice(row.cost)} ₺</dd></div>
        <div><dt>Atölye maliyeti</dt><dd>− {formatPrice(atolyeNum)} ₺</dd></div>
        <div><dt>Montaj maliyeti</dt><dd>− {formatPrice(montajNum)} ₺</dd></div>
        <div className={`oc-breakdown-total${profit < 0 ? ' is-negative' : ''}`}>
          <dt>Net kazanç{dirty ? ' (kaydedilmedi)' : ''}</dt>
          <dd>{formatPrice(profit)} ₺</dd>
        </div>
      </dl>
    </div>
  );
};

const ChevronIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="m6 9 6 6 6-6" />
  </svg>
);

const OrderCosts = () => {
  const [customers, setCustomers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateRange, setDateRange] = useState('all');
  const [sortOrder, setSortOrder] = useState('date-desc');
  const [openIds, setOpenIds] = useState(() => new Set());
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  useEffect(() => {
    let cancelled = false;
    Promise.all([getLiveCollection('customers'), getLiveCollection('orders')])
      .then(([customerSnap, orderSnap]) => {
        if (cancelled) return;
        setCustomers(customerSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        setOrders(orderSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      })
      .catch((err) => {
        console.error('Sipariş maliyetleri yüklenemedi:', err);
        if (!cancelled) setError('Sipariş maliyetleri yüklenemedi. Sayfayı yenileyip tekrar deneyin.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // Müşteri bazında grupla
  const rows = useMemo(() => {
    const byCustomer = new Map();
    orders.forEach((order) => {
      if (!order.customerId) return;
      if (!byCustomer.has(order.customerId)) byCustomer.set(order.customerId, []);
      byCustomer.get(order.customerId).push(order);
    });
    return customers
      .filter(c => byCustomer.has(c.id))
      .map((customer) => {
        const customerOrders = byCustomer.get(customer.id);
        const amount = getCustomerGrandTotal(customer, customerOrders);
        const cost = customerOrders.reduce((s, o) => s + getOrderCost(o), 0);
        const atolyeCost = parseFloat(customer.atolyeMaliyeti) || 0;
        const montajCost = parseFloat(customer.montajMaliyeti) || 0;
        return {
          id: customer.id,
          name: customer.name || 'İsimsiz müşteri',
          status: customer.orderStatus,
          orderDate: toDate(customer.orderDate),
          orders: customerOrders,
          cost,
          amount,
          atolyeCost,
          montajCost,
          profit: amount - cost - atolyeCost - montajCost,
          missingCount: customerOrders.filter(o => getOrderCost(o) <= 0).length,
        };
      });
  }, [customers, orders]);

  const filteredRows = useMemo(() => {
    const term = normalizeSearchText(searchTerm);
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const yearStart = new Date(now.getFullYear(), 0, 1);

    const list = rows.filter((row) => {
      if (statusFilter !== 'all' && row.status !== statusFilter) return false;
      if (dateRange !== 'all') {
        if (!row.orderDate) return false;
        if (dateRange === 'this-month' && row.orderDate < monthStart) return false;
        if (dateRange === 'last-month' && (row.orderDate < lastMonthStart || row.orderDate >= monthStart)) return false;
        if (dateRange === 'this-year' && row.orderDate < yearStart) return false;
      }
      if (term) {
        const haystack = [row.name, ...row.orders.map(o => `${getProductName(o)} ${o.roomName || ''}`)].join(' ');
        if (!normalizeSearchText(haystack).includes(term)) return false;
      }
      return true;
    });

    const time = (d) => (d ? d.getTime() : 0);
    list.sort((a, b) => {
      if (sortOrder === 'cost-desc') return b.cost - a.cost;
      if (sortOrder === 'cost-asc') return a.cost - b.cost;
      if (sortOrder === 'profit-desc') return b.profit - a.profit;
      if (sortOrder === 'profit-asc') return a.profit - b.profit;
      if (sortOrder === 'date-asc') return time(a.orderDate) - time(b.orderDate);
      return time(b.orderDate) - time(a.orderDate);
    });
    return list;
  }, [rows, searchTerm, statusFilter, dateRange, sortOrder]);

  useEffect(() => { setVisibleCount(PAGE_SIZE); }, [searchTerm, statusFilter, dateRange, sortOrder]);

  const totals = useMemo(() => filteredRows.reduce((acc, row) => ({
    cost: acc.cost + row.cost,
    amount: acc.amount + row.amount,
    extra: acc.extra + row.atolyeCost + row.montajCost,
    profit: acc.profit + row.profit,
    orderCount: acc.orderCount + row.orders.length,
    missing: acc.missing + row.missingCount,
  }), { cost: 0, amount: 0, extra: 0, profit: 0, orderCount: 0, missing: 0 }), [filteredRows]);

  const handleExtraSaved = (customerId, values) => {
    setCustomers(prev => prev.map(c => (c.id === customerId ? { ...c, ...values } : c)));
  };

  const toggle = (id) => setOpenIds((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const visibleRows = filteredRows.slice(0, visibleCount);

  return (
    <div className="oc">
      <div className="oc-info" role="note">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="12" cy="12" r="10" /><path d="M12 16v-4M12 8h.01" />
        </svg>
        <span>Bu sekme yalnızca kontrol içindir. Net kazanç = genel toplam (net) − sipariş maliyeti − atölye − montaj. Buradaki rakamlar başka hiçbir hesaplamaya eklenmez.</span>
      </div>

      <div className="oc-stats">
        <div className="oc-stat oc-stat-cost">
          <span className="oc-stat-label">Toplam sipariş maliyeti</span>
          <strong className="oc-stat-value">{formatPrice(totals.cost)} ₺</strong>
          <span className="oc-stat-sub">{filteredRows.length} müşteri · {totals.orderCount} sipariş</span>
        </div>
        <div className="oc-stat oc-stat-amount">
          <span className="oc-stat-label">Genel toplam (net)</span>
          <strong className="oc-stat-value">{formatPrice(totals.amount)} ₺</strong>
          <span className="oc-stat-sub">İskonto / vade / manuel toplam dahil</span>
        </div>
        <div className={`oc-stat oc-stat-profit${totals.profit < 0 ? ' is-negative' : ''}`}>
          <span className="oc-stat-label">Net kazanç</span>
          <strong className="oc-stat-value">{formatPrice(totals.profit)} ₺</strong>
          <span className="oc-stat-sub">Atölye + montaj: {formatPrice(totals.extra)} ₺</span>
        </div>
        <div className={`oc-stat oc-stat-missing${totals.missing > 0 ? ' has-missing' : ''}`}>
          <span className="oc-stat-label">Maliyeti girilmemiş</span>
          <strong className="oc-stat-value">{totals.missing}</strong>
          <span className="oc-stat-sub">{totals.missing > 0 ? 'sipariş kontrol edilmeli' : 'Tüm siparişlerde maliyet var'}</span>
        </div>
      </div>

      <div className="expenses-toolbar oc-toolbar">
        <div className="toolbar-inline-group">
          <div className="search-box">
            <i className="fas fa-search"></i>
            <input
              type="text"
              placeholder="Müşteri veya ürün ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <ModernSelect
            className="filter-select"
            ariaLabel="Sipariş durumu"
            value={statusFilter}
            onChange={setStatusFilter}
            options={[{ value: 'all', label: 'Tüm Durumlar' }, ...Object.entries(ORDER_STATUS_LABELS).map(([value, label]) => ({ value, label }))]}
          />
          <ModernSelect
            className="filter-select"
            ariaLabel="Tarih aralığı"
            value={dateRange}
            onChange={setDateRange}
            options={[
              { value: 'all', label: 'Tüm Zamanlar' },
              { value: 'this-month', label: 'Bu Ay' },
              { value: 'last-month', label: 'Geçen Ay' },
              { value: 'this-year', label: 'Bu Yıl' }
            ]}
          />
          <ModernSelect
            className="filter-select"
            ariaLabel="Sıralama"
            value={sortOrder}
            onChange={setSortOrder}
            options={[
              { value: 'date-desc', label: 'Tarih: Yeni - Eski' },
              { value: 'date-asc', label: 'Tarih: Eski - Yeni' },
              { value: 'cost-desc', label: 'Maliyet: Yüksek - Düşük' },
              { value: 'cost-asc', label: 'Maliyet: Düşük - Yüksek' },
              { value: 'profit-desc', label: 'Net Kazanç: Yüksek - Düşük' },
              { value: 'profit-asc', label: 'Net Kazanç: Düşük - Yüksek' }
            ]}
          />
        </div>
      </div>

      {loading ? (
        <div className="oc-empty"><div className="expense-spinner"></div><p>Sipariş maliyetleri yükleniyor...</p></div>
      ) : error ? (
        <div className="oc-empty oc-error"><p>{error}</p></div>
      ) : filteredRows.length === 0 ? (
        <div className="oc-empty"><p>Bu kriterlere uygun sipariş bulunamadı.</p></div>
      ) : (
        <ul className="oc-list">
          {visibleRows.map((row) => {
            const isOpen = openIds.has(row.id);
            return (
              <li key={row.id} className={`oc-customer${isOpen ? ' is-open' : ''}`}>
                <button type="button" className="oc-customer-head" onClick={() => toggle(row.id)} aria-expanded={isOpen}>
                  <span className="oc-customer-main">
                    <span className="oc-customer-name">{row.name}</span>
                    <span className="oc-customer-meta">
                      {row.status && (
                        <span className="oc-status" data-status={row.status}>{ORDER_STATUS_LABELS[row.status] || row.status}</span>
                      )}
                      {row.orderDate && <span>{formatDate(row.orderDate)}</span>}
                      <span>{row.orders.length} sipariş</span>
                      {row.missingCount > 0 && <span className="oc-missing">{row.missingCount} maliyetsiz</span>}
                    </span>
                  </span>
                  <span className="oc-customer-figures">
                    <span className="oc-figure">
                      <span className="oc-figure-label">Tutar</span>
                      <span className="oc-figure-amount">{formatPrice(row.amount)} ₺</span>
                    </span>
                    <span className="oc-figure">
                      <span className="oc-figure-label">Maliyet</span>
                      <strong className="oc-figure-cost">{formatPrice(row.cost)} ₺</strong>
                    </span>
                    <span className="oc-figure">
                      <span className="oc-figure-label">Net kazanç</span>
                      <strong className={`oc-figure-profit${row.profit < 0 ? ' is-negative' : ''}`}>{formatPrice(row.profit)} ₺</strong>
                    </span>
                    <span className="oc-chevron"><ChevronIcon /></span>
                  </span>
                </button>

                {isOpen && (
                  <>
                  <div className="oc-orders">
                    <div className="oc-order oc-order-head" aria-hidden="true">
                      <span>Ürün</span>
                      <span className="oc-num">Adet</span>
                      <span className="oc-num">Tutar</span>
                      <span className="oc-num">Maliyet</span>
                    </div>
                    {row.orders.map((order) => {
                      const cost = getOrderCost(order);
                      return (
                        <div key={order.id} className="oc-order">
                          <span className="oc-order-product">
                            <span className="oc-order-tags">
                              <span className="oc-type" data-type={order.productType}>
                                {PRODUCT_TYPES[order.productType]?.label || order.productType || 'Ürün'}
                              </span>
                              {order.roomName && <span className="oc-room">{order.roomName}</span>}
                            </span>
                            <span className="oc-order-name">{getProductName(order) || '—'}</span>
                          </span>
                          <span className="oc-num" data-label="Adet">{getOrderQuantity(order)}</span>
                          <span className="oc-num oc-muted" data-label="Tutar">{formatPrice(getOrderAmount(order))} ₺</span>
                          <span className="oc-num" data-label="Maliyet">
                            {cost > 0
                              ? <strong>{formatPrice(cost)} ₺</strong>
                              : <span className="oc-missing">Maliyet yok</span>}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                  <ExtraCostsEditor row={row} onSaved={handleExtraSaved} />
                  </>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {!loading && filteredRows.length > visibleCount && (
        <button type="button" className="oc-more" onClick={() => setVisibleCount(c => c + PAGE_SIZE)}>
          Daha fazla göster ({filteredRows.length - visibleCount} müşteri daha)
        </button>
      )}
    </div>
  );
};

export default OrderCosts;
